import { useCallback, useContext, useMemo } from 'react';
import { createId } from '@paralleldrive/cuid2';
import type { Chat, InputAttachment } from '@trinity/chat';
import { ChatQueryStatus } from '@trinity/chat';
import { transformRemirrorState } from '@trinity/chat-components/components/Remirror/utils/transform-remirror-state';
import {
  SubmitMessageContext,
  ChatSessionInputContext,
  ChatHasAccessContext,
  ChatSessionQueriesContext,
  ChatSessionPendingHintPromptIdentifierContext,
  EditorDocContext,
  ChatSessionPendingAttachmentsContext,
} from './context';
import { useSelectedQueryParentIdentifier } from './hooks/useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from './hooks/useSubmitQueryMutation';
import type { Context } from './interfaces.context';

type Props = {
  children: React.ReactNode;
};

/**
 * Returns the identifier of the first in-progress query, or null if none.
 */
const getInProgressQueryIdentifier = (queries: Record<string, Chat.Query>): string | null => {
  if (!queries) return null;

  const found = Object.values(queries).find(
    (query) =>
      query.status === ChatQueryStatus.Loading ||
      query.status === ChatQueryStatus.LoadingExtended ||
      query.status === ChatQueryStatus.Streaming
  );

  return found?.identifier ?? null;
};

export const SubmitMessageContainer = (props: Props) => {
  const input = useContext(ChatSessionInputContext);
  const queries = useContext(ChatSessionQueriesContext);
  const pendingHintPromptIdentifier = useContext(ChatSessionPendingHintPromptIdentifierContext);
  const editorDocRef = useContext(EditorDocContext);
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();
  const hasAccess = useContext(ChatHasAccessContext);
  const pendingAttachments = useContext(ChatSessionPendingAttachmentsContext);

  const { isPending, mutateAsync } = useSubmitQueryMutation();

  const handleSubmit = useCallback(async () => {
    const identifier = createId();
    const parentQueryIdentifier = getSelectedQueryParentIdentifier();

    const editorDoc = editorDocRef.current;
    const { text: queryText, mentions } = editorDoc
      ? transformRemirrorState(editorDoc)
      : { text: input, mentions: [] };

    const hasSubmittableAttachments = pendingAttachments.some((a) => a.status === 'success');
    if (queryText.trim().length === 0 && !hasSubmittableAttachments) {
      return;
    }

    // Attachments are uploaded at attach time, so submit only collects what is already in S3.
    const existingAttachments: InputAttachment[] = pendingAttachments
      .filter((a): a is typeof a & { fileUri: string } => a.status === 'success' && !!a.fileUri)
      .map((a) => ({
        type: 'file' as const,
        identifier: a.identifier,
        fileUri: a.fileUri,
        name: a.name,
        mimeType: a.mimeType as InputAttachment['mimeType'],
        sizeBytes: a.sizeBytes,
      }));

    const inputAttachments: InputAttachment[] | undefined =
      existingAttachments.length > 0 ? existingAttachments : undefined;

    return mutateAsync({
      action: null,
      identifier,
      parentQueryIdentifier,
      query: queryText,
      promptIdentifier: pendingHintPromptIdentifier ?? undefined,
      mentions: mentions.length > 0 ? mentions : undefined,
      inputAttachments,
    });
  }, [editorDocRef, getSelectedQueryParentIdentifier, input, mutateAsync, pendingHintPromptIdentifier, pendingAttachments]);

  const submitState = useMemo((): Context.SubmitMessageMetadata => {
    const inProgressIdentifier = getInProgressQueryIdentifier(queries);

    if (isPending || inProgressIdentifier !== null) {
      return { status: 'submitting-query', queryIdentifier: inProgressIdentifier };
    }

    // `success` with no extraction yet is the window between the S3 upload finishing and the
    // extract call answering — still busy, or the button flickers enabled in between.
    const hasExtractingAttachment = pendingAttachments.some(
      (a) => a.extraction?.status === 'pending' || (a.status === 'success' && a.extraction === undefined)
    );

    if (
      pendingAttachments.some((a) => a.status === 'uploading' || a.status === 'staged') ||
      hasExtractingAttachment
    ) {
      return { status: 'uploading-file' };
    }

    const hasSubmittableAttachments = pendingAttachments.some((a) => a.status === 'success');
    if ((input.trim().length === 0 && !hasSubmittableAttachments) || !hasAccess) {
      return { status: 'disabled' };
    }

    return { status: 'inactive' };
  }, [hasAccess, input, isPending, pendingAttachments, queries]);

  const submitCtx = useMemo(
    () => ({
      state: submitState,
      onSubmit: handleSubmit,
    }),
    [submitState, handleSubmit]
  );

  return <SubmitMessageContext.Provider value={submitCtx}>{props.children}</SubmitMessageContext.Provider>;
};
